import { useCallback } from 'react';
import { type UseTranslationResponse, useTranslation } from 'react-i18next';

import type { Locale } from './i18n';

export function useIntl() {
  // We keep a single `common` namespace that contains the full tree.
  // Not passing an ns preserves strong key inference from the defaultNS.
  const { t, i18n } = useTranslation();
  const translate = useCallback<TranslateFn>(
    (key, options) => t(key as unknown as never, options as unknown as never) as unknown as string,
    [t]
  );
  const change = useCallback((next: Locale) => i18n.changeLanguage(next), [i18n]);
  const locale = i18n.language as Locale;
  const tc = (key: Parameters<typeof t>[0], count: number, options?: Record<string, unknown>) => {
    const suffix = count === 1 ? '.one' : '.other';
    const pluralKey = (key as string) + suffix;
    return t(pluralKey as unknown as never, { count, ...(options || {}) });
  };
  return { t: translate, tc, locale, change } as const;
}

export type { UseTranslationResponse };
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;
export { useTranslation };
